import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { 
  AlertTriangle, FileText, CheckCircle, Clock, RefreshCw,
  TrendingUp, Building2, ClipboardList, FileCheck, FolderOpen,
  AlertCircle, ChevronRight, BarChart3, GitBranch, Settings
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

const KPIMetrics = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/kpi-dashboard/metrics');
      setMetrics(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching KPI metrics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 300000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const prepareChartData = (dataObj) => {
    if (!dataObj) return [];
    return Object.entries(dataObj).map(([name, value]) => ({
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      fullName: name,
      value
    }));
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading KPI Dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Dashboard</h1>
          <p className="text-gray-500 text-sm">
            Quality Management Key Performance Indicators
            {lastUpdated && (
              <span className="ml-2 text-gray-400">
                • Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchMetrics}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {/* Open & Pending ONCRs */}
        <div 
          className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate('/oncrs')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Open & Pending ONCRs</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {metrics?.oncr?.open_and_pending || 0}
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              {metrics?.oncr?.pending || 0} pending
            </span>
          </div>
        </div>

        {/* Open & Pending CAPAs */}
        <div 
          className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate('/capas')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <ClipboardList className="h-6 w-6 text-red-600" />
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Open & Pending CAPAs</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {metrics?.capa?.open_and_pending || 0}
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
              {metrics?.capa?.pending || 0} pending verification
            </span>
          </div>
        </div>

        {/* Documents Needing Approval */}
        <div 
          className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate('/documents')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileCheck className="h-6 w-6 text-blue-600" />
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Documents Need Approval</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {metrics?.documents?.need_approval || 0}
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {metrics?.documents?.in_review || 0} in review
            </span>
          </div>
        </div>

        {/* Open NCRs */}
        <div 
          className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate('/ncrs')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-rose-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-rose-600" />
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Open NCRs</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {metrics?.ncr?.open || 0}
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm">
            {Object.entries(metrics?.ncr?.by_severity || {}).slice(0, 2).map(([sev, count]) => (
              <span key={sev} className={`px-2 py-0.5 rounded text-xs ${
                sev === 'critical' ? 'bg-red-100 text-red-700' :
                sev === 'major' ? 'bg-orange-100 text-orange-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {count} {sev}
              </span>
            ))}
          </div>
        </div>

        {/* Change Management */}
        <div 
          className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate('/change-management')}
          data-testid="change-management-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <GitBranch className="h-6 w-6 text-purple-600" />
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Change Requests</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {metrics?.change_management?.pending_review || 0}
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              {metrics?.change_management?.pending_review || 0} pending review
            </span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {metrics?.change_management?.implementing || 0} implementing
            </span>
          </div>
        </div>
      </div>

      {/* New Documents Section */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <FolderOpen className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">New Revisions / New Documents (BMS Folder)</h3>
            <p className="text-sm text-gray-500">Last 30 days</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-green-700">{metrics?.documents?.new_documents || 0}</p>
            <p className="text-sm text-green-600">New Documents</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-blue-700">{metrics?.documents?.new_revisions || 0}</p>
            <p className="text-sm text-blue-600">New Revisions</p>
          </div>
        </div>
        {metrics?.documents?.new_docs_list?.length > 0 && (
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-600 mb-2">Recent Documents:</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {metrics.documents.new_docs_list.slice(0, 5).map((doc, idx) => (
                <div key={doc.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-700 block truncate">
                        {doc.name || doc.title || doc.document_number || 'Untitled Document'}
                      </span>
                      {doc.description && (
                        <span className="text-xs text-gray-500 block truncate">
                          {doc.description.substring(0, 60)}{doc.description.length > 60 ? '...' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">Rev {doc.revision || '1'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ONCRs by Department */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-800">ONCRs by Department</h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={prepareChartData(metrics?.oncr?.by_department)} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={80} />
              <Tooltip 
                formatter={(value, name, props) => [value, props.payload.fullName]}
                contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {prepareChartData(metrics?.oncr?.by_department).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* CAPAs by Department */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800">CAPAs by Department</h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={prepareChartData(metrics?.capa?.by_department)} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={80} />
              <Tooltip 
                formatter={(value, name, props) => [value, props.payload.fullName]}
                contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {prepareChartData(metrics?.capa?.by_department).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Change Management Section */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <GitBranch className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Change Management Overview</h3>
              <p className="text-sm text-gray-500">Approval workflow status</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/change-management')}
            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            View All →
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-yellow-700">{metrics?.change_management?.pending_review || 0}</p>
              <p className="text-xs text-yellow-600 font-medium">Pending Review</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{metrics?.change_management?.approved || 0}</p>
              <p className="text-xs text-green-600 font-medium">Approved</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{metrics?.change_management?.implementing || 0}</p>
              <p className="text-xs text-blue-600 font-medium">Implementing</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{metrics?.change_management?.completed || 0}</p>
              <p className="text-xs text-purple-600 font-medium">Completed</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{metrics?.change_management?.rejected || 0}</p>
              <p className="text-xs text-red-600 font-medium">Rejected</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{metrics?.change_management?.total || 0}</p>
              <p className="text-xs text-gray-600 font-medium">Total</p>
            </div>
          </div>
          
          {/* Change Status Chart */}
          <div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={prepareChartData(metrics?.change_management?.by_status)} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={80} />
                <Tooltip 
                  formatter={(value, name, props) => [value, props.payload.fullName]}
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {prepareChartData(metrics?.change_management?.by_status).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Changes Needing Attention */}
        {metrics?.change_management?.recent_changes?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-600 mb-2">Recent Changes Needing Attention:</p>
            <div className="space-y-2">
              {metrics.change_management.recent_changes.slice(0, 3).map((change, idx) => (
                <div key={change.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-700">{change.title}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      change.impact_level === 'high' ? 'bg-red-100 text-red-700' :
                      change.impact_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {change.impact_level}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      change.status === 'review' ? 'bg-yellow-100 text-yellow-700' :
                      change.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {change.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Documents Pending Approval List */}
      {metrics?.documents?.pending_list?.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Documents Awaiting Approval</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Document</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Number</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {metrics.documents.pending_list.map((doc, idx) => (
                  <tr key={doc.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-800">{doc.name || doc.title || doc.document_number}</td>
                    <td className="py-2 px-3 text-gray-600">{doc.document_number}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        doc.status === 'Pending Approval' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-500">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIMetrics;
